import { CardActions, CardContent, DialogContent, DialogTitle } from '@mui/material';
import { useConfig, useNotification, useOidcWithAdmin } from '@revenuewell/front-end-bundle';
import { PrimaryButton, SecondaryButton } from 'apps/wrapper/src/components/shared/buttons/buttons.styles';
import { Card } from 'apps/wrapper/src/components/shared/card/card.style';
import { Dialog } from 'apps/wrapper/src/components/shared/dialog/dialog.styles';
import AppointmentService from 'apps/wrapper/src/services/appointment/appointment-service';
import { AppointmentRequestNote } from 'apps/wrapper/src/services/appointment/types/appointment-request-note';
import { FC, useMemo, useState } from 'react';
import { CloseDialogButton, DialogContentWrapper, NoteText } from './add-note-dialog.styles';

export interface AddNoteDialogProps {
  opened: boolean;
  appointmentRequestId: number;
  locationId: number;
  notes: AppointmentRequestNote[];
  onClose: () => void;
  onAddNode: () => void;
}

export const AddNoteDialog: FC<AddNoteDialogProps> = (props) => {
  const { opened, appointmentRequestId, locationId, notes, onClose, onAddNode } = props;
  const { config } = useConfig();
  const { oidcService } = useOidcWithAdmin();
  const { getClaims } = useOidcWithAdmin();
  const { showError } = useNotification();
  const [text, setText] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const appointmentService = useMemo(() => new AppointmentService(config, oidcService), [config, oidcService]);

  const handleSave = async () => {
    setIsLoading(true);

    try {
      const claims = await getClaims();
      const authorName = `${claims.firstName} ${claims.lastName}`;
      const newNote = {
        appointmentRequestId,
        locationId,
        note: text,
        name: authorName,
        systemTimestamp: new Date()
      } as AppointmentRequestNote;

      await appointmentService.updateAppointmentRequestNotes(appointmentRequestId, locationId, [...notes, newNote]);
      onAddNode();
    } catch (e) {
      showError('Unable to save note.');
      handleClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setText('');
    onClose();
  };

  return (
    <>
      <Dialog open={opened} maxWidth={false}>
        <DialogContentWrapper>
          <DialogTitle sx={{ cursor: 'default' }}>
            Add a Note
            <CloseDialogButton onClick={handleClose} />
          </DialogTitle>
          <DialogContent>
            <Card>
              <CardContent>
                <NoteText
                  value={text}
                  label="Note"
                  multiline
                  rows={4}
                  inputProps={{ maxLength: 400 }}
                  onChange={(e: any) => setText(e.target.value)}
                />
                <CardActions sx={{ padding: '0 8px' }}>
                  <SecondaryButton
                    onClick={handleClose}
                    color="primary"
                    variant="outlined"
                    disableElevation
                    sx={{ marginRight: '10px' }}
                  >
                    CANCEL
                  </SecondaryButton>
                  <PrimaryButton
                    loading={isLoading}
                    onClick={handleSave}
                    color="primary"
                    variant="contained"
                    disableElevation
                    disabled={text.trim().length === 0}
                  >
                    SAVE
                  </PrimaryButton>
                </CardActions>
              </CardContent>
            </Card>
          </DialogContent>
        </DialogContentWrapper>
      </Dialog>
    </>
  );
};
